// /cli/auth — CLI device-flow approval page.
//
// The `gated` CLI prints `Open <dashboard>/cli/auth?code=ABCD-EFGH`. The user
// opens it here (already signed in — the root route guards auth), visually
// matches the code against their terminal, and clicks Approve. That POSTs the
// user_code to /api/cli/auth/approve, which the platform authorizes via the
// Clerk session + active org.
//
// Multi-org note: this page only DISPLAYS the active org. A user who wants the
// CLI bound to a different org switches org with the dashboard's existing org
// switcher, then approves. No org picker here (per the device-flow spec).

import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useOrganization } from "@clerk/react";
import { z } from "zod";
import { Button, Card, Input, LoadingState } from "@/design-system";
import { approveCliAuth } from "@/api/cliAuth";
import { track } from "@/lib/analytics";

const searchSchema = z.object({
  code: z.string().optional(),
});

export const Route = createFileRoute("/cli/auth")({
  validateSearch: searchSchema,
  // Render without the dashboard sidebar — this is a focused, single-action
  // approval page reached from the terminal, so it uses the centered auth shell.
  staticData: { shell: "auth" },
  component: CliAuthPage,
});

type Phase = "idle" | "submitting" | "success" | "error";

function errorMessageFor(code: string): string {
  switch (code) {
    case "device_code_invalid":
      return "That code wasn't found. Check it and try again.";
    case "device_code_expired":
      return "That code has expired — run `gated login` again.";
    case "device_code_consumed":
      return "That code was already used.";
    default:
      return "Something went wrong approving the CLI. Try again.";
  }
}

function CliAuthPage() {
  const { code: codeFromUrl } = Route.useSearch();
  const { isLoaded, organization } = useOrganization();

  // When the URL carries no code, the user pastes it manually.
  const [manualCode, setManualCode] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [errorCode, setErrorCode] = useState<string>("unknown");
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the code input on mount when this is the manual-entry
  // path (no `code` query param). Bare `autoFocus` is a11y-flagged
  // because it can disorient screen-reader users on unrelated pages —
  // here the page exists solely to capture this input, so a focused
  // `useEffect` is the right scoped pattern.
  useEffect(() => {
    if (!codeFromUrl) codeInputRef.current?.focus();
  }, [codeFromUrl]);

  if (!isLoaded) {
    return <LoadingState />;
  }

  const code = (codeFromUrl ?? manualCode).trim();
  const canSubmit = code.length > 0 && phase !== "submitting";

  const handleApprove = async () => {
    if (!code) return;
    setPhase("submitting");
    try {
      await approveCliAuth(code);
      track({ event: "cli_auth_approved" });
      setPhase("success");
    } catch (err) {
      setErrorCode(err instanceof Error ? err.message : "unknown");
      setPhase("error");
    }
  };

  return (
    <Card className="w-full max-w-md p-8 flex flex-col gap-6">
      {phase === "success" ? (
            <div className="flex flex-col gap-2">
              <h1 className="text-[18px] font-medium text-fg-1">
                CLI authorized
              </h1>
              <p className="text-[14px] text-fg-2 leading-relaxed">
                Return to your terminal; the <code className="font-mono">gated</code>{" "}
                CLI is now signed in
                {organization?.name ? (
                  <>
                    {" "}
                    to <span className="text-fg-1">{organization.name}</span>
                  </>
                ) : null}
                .
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <h1 className="text-[18px] font-medium text-fg-1">
                  Authorize the Gated CLI
                </h1>
                <p className="text-[14px] text-fg-2 leading-relaxed">
                  Confirm the code below matches the one printed in your
                  terminal, then approve to sign the CLI in
                  {organization?.name ? (
                    <>
                      {" "}
                      to{" "}
                      <span className="text-fg-1">{organization.name}</span>
                    </>
                  ) : null}
                  .
                </p>
              </div>

              {codeFromUrl ? (
                <div className="flex flex-col gap-1.5">
                  <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-fg-3">
                    Code from your terminal
                  </span>
                  <div className="border border-border-2 rounded-[var(--radius-2)] bg-bg-2 px-4 py-3">
                    <span className="font-mono text-[22px] tracking-[0.12em] text-fg-1">
                      {codeFromUrl}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="cli-auth-code"
                    className="font-mono text-[11px] uppercase tracking-[0.08em] text-fg-3"
                  >
                    Enter the code from your terminal
                  </label>
                  <Input
                    id="cli-auth-code"
                    ref={codeInputRef}
                    inputSize="lg"
                    placeholder="ABCD-EFGH"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    disabled={phase === "submitting"}
                  />
                </div>
              )}

              {phase === "error" ? (
                <p className="text-small text-sev-crit">
                  {errorMessageFor(errorCode)}
                </p>
              ) : null}

              <Button
                variant="accent-strong"
                size="lg"
                onClick={() => void handleApprove()}
                disabled={!canSubmit}
              >
                {phase === "submitting" ? "Approving…" : "Approve"}
              </Button>
            </>
          )}
    </Card>
  );
}
